import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_STYLES = {
  COMPLETED: { bg: 'var(--status-completed-bg)', color: 'var(--status-completed-text)', label: 'Completed' },
  ON_TRACK: { bg: 'var(--status-on-track-bg)', color: 'var(--status-on-track-text)', label: 'On Track' },
  AHEAD: { bg: 'var(--status-ahead-bg)', color: 'var(--status-ahead-text)', label: 'Ahead' },
  DELAYED: { bg: 'var(--status-delayed-bg)', color: 'var(--status-delayed-text)', label: 'Delayed' },
  OVERDUE: { bg: 'var(--status-delayed-bg)', color: 'var(--status-delayed-text)', label: 'Overdue' },
  IN_PROGRESS: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: 'In Progress' },
  NOT_STARTED: { bg: 'var(--badge-muted-bg)', color: 'var(--text-disabled)', label: 'Not Started' },
};

function StatusCellRenderer(params) {
  const status = params.value;
  if (!status) return null;
  const s = STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED;
  return (
    <span data-testid={`status-badge-${params.data?.id}`}
      style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function ProgressCellRenderer(params) {
  const value = params.value ?? 0;
  const status = params.data?.overall_status;
  const s = STATUS_STYLES[status] || STATUS_STYLES.ON_TRACK;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
      <div style={{ flex: 1, height: '6px', backgroundColor: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', backgroundColor: s.color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
      </div>
      <span style={{ fontSize: '11px', color: s.color, fontWeight: 600, minWidth: '32px', textAlign: 'right' }}>
        {value.toFixed(0)}%
      </span>
    </div>
  );
}

function DocTypeCellRenderer(params) {
  if (!params.value) return null;
  return (
    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
      backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)', border: '1px solid rgba(56, 96, 255, 0.15)' }}>
      {params.value}
    </span>
  );
}

function StageGateDateCellRenderer(params) {
  if (!params.value) return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
  const date = new Date(params.value);
  const formatted = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  const colId = params.column?.getColId() || '';
  let color = 'var(--text-secondary)';
  if (colId.includes('_actual')) {
    color = 'var(--status-completed-text)';
  } else if (colId.includes('_forecast')) {
    const sgId = colId.replace('_forecast', '');
    const plannedVal = params.data?.[sgId + '_planned'];
    if (plannedVal && new Date(params.value) > new Date(plannedVal)) {
      color = 'var(--status-delayed-text)';
    }
  }
  return <span style={{ color, fontSize: '12px' }}>{formatted}</span>;
}

function StageGateStatusRenderer(params) {
  const status = params.value;
  if (!status) return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
  const s = STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED;
  return (
    <span style={{ padding: '1px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 600, backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function DateEditor(props) {
  const [value, setValue] = useState(props.value ? props.value.split('T')[0] : '');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const handleChange = (e) => {
    const newVal = e.target.value;
    setValue(newVal);
    if (props.onValueChange) props.onValueChange(newVal ? new Date(newVal).toISOString() : null);
  };
  return (
    <input ref={inputRef} type="date" value={value} onChange={handleChange}
      style={{ width: '100%', backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)',
        border: '1px solid var(--accent)', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', outline: 'none' }} />
  );
}

export default function DCIMatrixGrid({ entries, stageGateConfigs, selectedDocType, projectId, onRefresh, isDark }) {
  const gridRef = useRef(null);

  const rowData = useMemo(() => {
    if (!entries) return [];
    return entries.map((entry) => {
      const row = {
        id: entry.id, document_number: entry.document_number, document_title: entry.document_title,
        document_type_code: entry.document_type_code, discipline: entry.discipline,
        area: entry.area || '', contractor: entry.contractor || '',
        overall_progress: entry.overall_progress || 0, overall_status: entry.overall_status, _raw: entry,
      };
      (entry.stage_gate_progress || []).forEach((sg) => {
        const p = sg.stage_gate_id;
        row[`${p}_planned`] = sg.planned_date;
        row[`${p}_forecast`] = sg.forecast_date;
        row[`${p}_actual`] = sg.actual_date;
        row[`${p}_status`] = sg.status;
        row[`${p}_name`] = sg.stage_gate_name;
        row[`${p}_weight`] = sg.weightage_percent;
      });
      return row;
    });
  }, [entries]);

  const stageGateColumns = useMemo(() => {
    if (!stageGateConfigs || stageGateConfigs.length === 0) return [];
    if (selectedDocType) {
      const config = stageGateConfigs.find((c) => c.document_type_id === selectedDocType || c.document_type_code === selectedDocType);
      if (config) return config.stage_gates.map((sg) => ({ id: sg.stage_gate_id, name: sg.stage_gate_name, weight: sg.weightage_percent }));
    }
    const gateMap = new Map();
    stageGateConfigs.forEach((config) => {
      (config.stage_gates || []).forEach((sg) => {
        if (!gateMap.has(sg.stage_gate_name)) {
          gateMap.set(sg.stage_gate_name, { ids: [sg.stage_gate_id], name: sg.stage_gate_name, weight: sg.weightage_percent });
        } else { gateMap.get(sg.stage_gate_name).ids.push(sg.stage_gate_id); }
      });
    });
    return Array.from(gateMap.values()).map((g) => ({ id: g.ids[0], ids: g.ids, name: g.name, weight: g.weight }));
  }, [stageGateConfigs, selectedDocType]);

  const handleCellValueChanged = useCallback(async (params) => {
    const { data, colDef, newValue } = params;
    const colId = colDef.field;
    if (!colId || !data._raw) return;
    const match = colId.match(/^(.+)_(planned|forecast|actual)$/);
    if (!match) return;
    const stageGateId = match[1];
    const dateField = match[2] + '_date';
    try {
      await api.put(`/projects/${projectId}/engineering-schedule/entries/${data.id}/stage-gates/${stageGateId}`, { [dateField]: newValue || null });
      toast.success('Date updated successfully');
      if (onRefresh) onRefresh();
    } catch (error) { toast.error('Failed to update date'); }
  }, [projectId, onRefresh]);

  const columnDefs = useMemo(() => {
    const fixedCols = [
      { headerName: '#', valueGetter: (p) => p.node.rowIndex + 1, width: 50, pinned: 'left', sortable: false, filter: false, suppressSizeToFit: true },
      { headerName: 'Document Number', field: 'document_number', pinned: 'left', minWidth: 160, width: 180, filter: 'agTextColumnFilter',
        cellStyle: { color: 'var(--accent-text)', fontWeight: 500 } },
      { headerName: 'Title', field: 'document_title', pinned: 'left', minWidth: 200, width: 260, filter: 'agTextColumnFilter', tooltipField: 'document_title' },
      { headerName: 'Type', field: 'document_type_code', width: 90, filter: 'agTextColumnFilter', cellRenderer: DocTypeCellRenderer },
      { headerName: 'Discipline', field: 'discipline', minWidth: 100, width: 120, filter: 'agTextColumnFilter' },
      { headerName: 'Area', field: 'area', minWidth: 80, width: 100, filter: 'agTextColumnFilter' },
      { headerName: 'Contractor', field: 'contractor', minWidth: 120, width: 140, filter: 'agTextColumnFilter' },
    ];

    const stageCols = stageGateColumns.map((gate) => {
      const gateIds = gate.ids || [gate.id];
      const mkGetter = (sfx) => (p) => { if (!p.data) return null; for (const gid of gateIds) { const v = p.data[`${gid}${sfx}`]; if (v != null) return v; } return null; };
      const mkSetter = (sfx) => (p) => { if (!p.data) return false; for (const gid of gateIds) { if (p.data[`${gid}${sfx}`] !== undefined || gateIds.length === 1) { p.data[`${gid}${sfx}`] = p.newValue; return true; } } p.data[`${gateIds[0]}${sfx}`] = p.newValue; return true; };
      return {
        headerName: `${gate.name} (${gate.weight}%)`,
        children: [
          { headerName: 'Plan', field: `${gate.id}_planned`, minWidth: 100, width: 110, valueGetter: mkGetter('_planned'), valueSetter: mkSetter('_planned'), cellRenderer: StageGateDateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Forecast', field: `${gate.id}_forecast`, minWidth: 100, width: 110, valueGetter: mkGetter('_forecast'), valueSetter: mkSetter('_forecast'), cellRenderer: StageGateDateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Actual', field: `${gate.id}_actual`, minWidth: 100, width: 110, valueGetter: mkGetter('_actual'), valueSetter: mkSetter('_actual'), cellRenderer: StageGateDateCellRenderer, editable: true, cellEditor: DateEditor, cellEditorPopup: false },
          { headerName: 'Status', field: `${gate.id}_status`, minWidth: 90, width: 95, valueGetter: mkGetter('_status'), cellRenderer: StageGateStatusRenderer, editable: false },
        ],
      };
    });

    const summaryCols = [
      { headerName: 'Progress', field: 'overall_progress', minWidth: 130, width: 140, cellRenderer: ProgressCellRenderer, sortable: true },
      { headerName: 'Status', field: 'overall_status', minWidth: 100, width: 110, cellRenderer: StatusCellRenderer, sortable: true },
    ];

    return [...fixedCols, ...stageCols, ...summaryCols];
  }, [stageGateColumns]);

  const defaultColDef = useMemo(() => ({ sortable: true, resizable: true, suppressMovable: false, editable: false }), []);
  const gridTheme = isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';

  return (
    <div className={gridTheme} style={{ width: '100%', height: '600px' }} data-testid="dci-matrix-grid">
      <AgGridReact ref={gridRef} rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
        onCellValueChanged={handleCellValueChanged} animateRows={true} rowSelection="multiple"
        suppressRowClickSelection={true} enableCellTextSelection={true} tooltipShowDelay={500}
        getRowId={(params) => params.data.id}
        overlayNoRowsTemplate='<span style="color: var(--text-muted); font-size: 14px;">No DCI entries found.</span>' />
    </div>
  );
}
